import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { useState } from 'react';

import {
  formatDate,
  formatMoney,
} from '../../../../../../../../lib/functions/utils';
import EntryModal from '../../../BookEntries/components/Modal';

const useStyles = makeStyles(() =>
  createStyles({
    tableCell: {
      padding: '8px !important',
      overflow: 'hidden',
      minWidth: '50px',
      maxWidth: '180px',
      whiteSpace: 'nowrap',
      fontSize: '14px !important',
    },
    clickableRow: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
  }),
);

interface MovementsTableProps {
  groupedLines: any[];
  groupedByCategoryAndBusiness: Record<string, Record<string, any[]>>;
}

const MovementsTable = ({
  groupedLines,
  groupedByCategoryAndBusiness,
}: MovementsTableProps) => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  const handleRowClick = (row: any) => {
    const foundGroup = findGroupByMovementKey(row);
    setSelectedCategory(foundGroup);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCategory(null);
  };

  const findGroupByMovementKey = (
    row: any,
  ): {
    date: string;
    category: string;
    groupedLines: any[];
    businessName: string;
    businessIdentifier: string;
  } | null => {
    const formattedDate = formatDate(row.date, 'yyyy-mm-dd');
    const dateGroups = groupedByCategoryAndBusiness?.[formattedDate];
    if (!dateGroups) {
      return null;
    }
    const allEntries = Object.values(dateGroups).flat();
    const matchingEntry = allEntries.find(
      (entry: any) => entry.movementKey === row.movementKey,
    );
    if (matchingEntry) {
      const categoryAndAccount = Object.keys(dateGroups).find((key) =>
        dateGroups[key].some(
          (entry: any) => entry.movementKey === row.movementKey,
        ),
      );
      return {
        date: formattedDate,
        category: matchingEntry.category,
        groupedLines: dateGroups[categoryAndAccount!],
        businessName: matchingEntry.businessName,
        businessIdentifier: matchingEntry.businessIdentifier,
      };
    }
    return null;
  };

  const handleExportToExcel = async () => {
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Libro Mayor');

    const accountName = groupedLines[0]?.accountName || 'Cuenta';

    // Agregar título
    const titleRow = worksheet.addRow([accountName]);
    titleRow.font = { bold: true, size: 14 };
    worksheet.mergeCells(1, 1, 1, 6);
    worksheet.addRow([]);

    // Headers
    const headers = [
      'Fecha',
      'Descripción',
      'Debe',
      'Haber',
      'Débito',
      'Crédito',
    ];
    const headerRow = worksheet.addRow(headers);

    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFDDDDDD' },
      };
    });

    // Configurar ancho de columnas
    worksheet.getColumn(1).width = 12; // Fecha
    worksheet.getColumn(2).width = 40; // Descripción
    worksheet.getColumn(3).width = 15; // Debe
    worksheet.getColumn(4).width = 15; // Haber
    worksheet.getColumn(5).width = 15; // Débito
    worksheet.getColumn(6).width = 15; // Crédito

    // Configurar formato de números para las columnas de montos
    [3, 4, 5, 6].forEach((col) => {
      worksheet.getColumn(col).numFmt = '#,##0';
    });

    // Agregar datos
    groupedLines.forEach((line: any) => {
      worksheet.addRow([
        formatDate(line.date),
        line.description || '',
        line.debit || 0,
        line.credit || 0,
        line.debitFinal || 0,
        line.creditFinal || 0,
      ]);
    });

    // Agregar fila de totales
    const totalsRow = worksheet.addRow([
      'Totales:',
      '',
      groupedLines.reduce(
        (sum: number, line: any) => sum + (line.debit || 0),
        0,
      ),
      groupedLines.reduce(
        (sum: number, line: any) => sum + (line.credit || 0),
        0,
      ),
      groupedLines.reduce(
        (sum: number, line: any) => sum + (line.debitFinal || 0),
        0,
      ),
      groupedLines.reduce(
        (sum: number, line: any) => sum + (line.creditFinal || 0),
        0,
      ),
    ]);

    totalsRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6E6E6' },
      };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'double' },
      };
    });

    // Guardar el archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `libro_mayor_${accountName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();

    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Paper elevation={0} style={{ padding: 16, margin: '12px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 8,
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {groupedLines[0].accountName}
          </Typography>
          <IconButton
            onClick={() => {
              void handleExportToExcel();
            }}
            size="small"
            color="primary"
            title="Exportar a Excel"
          >
            <FileDownloadIcon />
          </IconButton>
        </div>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID Asiento</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Debe</TableCell>
              <TableCell>Haber</TableCell>
              <TableCell>Débito</TableCell>
              <TableCell>Crédito</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedLines.map((category: any, catIndex: number) => (
              <React.Fragment key={catIndex}>
                <TableRow
                  className={classes.clickableRow}
                  onClick={() => handleRowClick(category)}
                >
                  <TableCell className={classes.tableCell}>
                    {category.entryId}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatDate(category.date)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {category.description}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.debit)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.credit)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.debitFinal)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.creditFinal)}
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            <TableRow>
              <TableCell
                colSpan={3}
                style={{
                  fontWeight: 'bold',
                  textAlign: 'left',
                }}
              >
                Totales:
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: any) => sum + line.debit,
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: any) => sum + line.credit,
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: any) => sum + line.debitFinal,
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: any) => sum + line.creditFinal,
                    0,
                  ),
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {selectedCategory && (
          <EntryModal
            open={modalOpen}
            onClose={handleCloseModal}
            entry={selectedCategory}
          />
        )}
      </Paper>
    </>
  );
};

export default MovementsTable;
